// Tipos de variable. Espeja el enum del backend (app/models.py).
const VARIABLE_TYPE = {
    NONE: 0,
    INT: 1,
    FLOAT: 2,
    STRING: 3,
    BATTERY: 4,
    BOOLEAN: 5,
};

function formatTimestamp(timestamp) {
    if(timestamp == null) return "-";
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB');
}

/**
 * Detecta el tipo de un objeto tipo variable.
 *
 * `item` suele ser un VariableData ({value, precision, variable: {type, data_type}}),
 * pero también acepta filas planas ({value, type, data_type}).
 *
 * @param options.looseBooleanDigits - si es true, las cadenas '1'/'0'/'1.0'/'0.0'
 *   se detectan como BOOLEAN. Por defecto false (se tratan como numéricas), que
 *   es la regla de la grilla de variables. Se pasa true para reproducir la regla
 *   más laxa que ya usaban dashboard.js y alarm_table_widget.js, de modo que
 *   este refactor no cambie el comportamiento en esos dos sitios.
 */
function getVariableType(item, options = {}) {
    const declared = Number(item?.variable?.type ?? item?.type);
    if (declared && !isNaN(declared)) return declared;

    // Respetar el data_type configurado en mqtt_config antes de auto-detectar
    const configured = item?.variable?.data_type ?? item?.data_type;
    if (configured === 'boolean') return VARIABLE_TYPE.BOOLEAN;
    if (configured === 'string') return VARIABLE_TYPE.STRING;
    if (configured === 'number') return VARIABLE_TYPE.FLOAT;

    // Fallback: auto-detectar por boolean explícito (true/false JS o string True/False)
    const raw = item?.value;
    const str = String(raw).toLowerCase().trim();
    if (raw === true || raw === false || str === 'true' || str === 'false') {
        return VARIABLE_TYPE.BOOLEAN;
    }
    if (options.looseBooleanDigits && ['1', '0', '1.0', '0.0'].includes(str)) {
        return VARIABLE_TYPE.BOOLEAN;
    }
    if (typeof raw === 'string' && isNaN(parseFloat(raw))) return VARIABLE_TYPE.STRING;
    return VARIABLE_TYPE.FLOAT;
}

/**
 * Etiquetas de estado efectivas de una variable booleana.
 *
 * Cadena de precedencia: override del dispositivo -> valor por defecto del tipo
 * de dispositivo -> los literales 'True'/'False'.
 *
 * Tolera null y objetos viejos de localStorage anteriores a esta funcionalidad.
 */
function getStateLabels(item) {
    const clean = (s) => (typeof s === 'string' ? s.trim() : '');
    return {
        trueLabel: clean(item?.true_label) || clean(item?.variable?.true_label) || 'True',
        falseLabel: clean(item?.false_label) || clean(item?.variable?.false_label) || 'False',
    };
}

/**
 * ¿Este valor crudo representa el estado verdadero?
 * Espeja la regla del backend (cualquier valor distinto de cero es verdadero).
 */
function isBooleanTrue(rawValue) {
    if (rawValue === true) return true;
    if (rawValue === false || rawValue === null || rawValue === undefined) return false;
    const str = String(rawValue).trim().toLowerCase();
    if (str === 'true') return true;
    if (str === 'false') return false;
    const num = parseFloat(str);
    return !isNaN(num) && num !== 0;
}

/**
 * {isTrue, label} para el valor actual (o el que se pase) de una variable booleana.
 */
function getBooleanLabel(item, rawValue) {
    const raw = rawValue === undefined ? item?.value : rawValue;
    const isTrue = isBooleanTrue(raw);
    const {trueLabel, falseLabel} = getStateLabels(item);
    return {isTrue, label: isTrue ? trueLabel : falseLabel};
}

export {
    VARIABLE_TYPE,
    formatTimestamp,
    getVariableType,
    getStateLabels,
    isBooleanTrue,
    getBooleanLabel,
}
